import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useLocation } from 'wouter'
import { useDropzone } from 'react-dropzone'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Tooltip } from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
import { apiRequest, formatBytes } from '@/lib/utils'
import { 
  Upload, 
  FileText, 
  X, 
  AlertCircle, 
  CheckCircle, 
  Loader2,
  ArrowLeft,
  Brain,
  Zap,
  GripVertical,
  ChevronUp,
  ChevronDown,
  FileX
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import AnalyticsHeader from '@/components/AnalyticsHeader'

export default function UploadPage() {
  const [files, setFiles] = useState([])
  const [aiProvider, setAiProvider] = useState('digitalocean') // Using DigitalOcean for cost optimization
  const [, setLocation] = useLocation()
  const { toast } = useToast()
  const [showUnsupportedModal, setShowUnsupportedModal] = useState(false)
  const [unsupportedFileError, setUnsupportedFileError] = useState(null)

  const uploadMutation = useMutation({
    mutationFn: async (formData) => {
      return apiRequest('/extract-dates', {
        method: 'POST',
        body: formData
      })
    },
    onSuccess: (data) => {
      if (data.success && data.jobId) {
        setLocation(`/results/${data.jobId}`)
      } else {
        // Check if this is a scanned PDF error
        if (data.isScannedPdf) {
          setUnsupportedFileError(data)
          setShowUnsupportedModal(true)
        } else {
          toast({
            title: "Upload Failed",
            description: data.error || "Unknown error occurred",
            variant: "destructive",
          })
        }
      }
    },
    onError: (error) => {
      // Check if this is a scanned PDF error
      if (error.data?.isScannedPdf) {
        setUnsupportedFileError(error.data)
        setShowUnsupportedModal(true)
      } else {
        toast({
          title: "Upload Failed", 
          description: error.message,
          variant: "destructive",
        })
      }
    }
  })

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    rejectedFiles.forEach(({ file, errors }) => {
      errors.forEach((error) => {
        if (error.code === 'file-too-large') {
          toast({
            title: "File Too Large",
            description: `${file.name} exceeds 20MB limit`,
            variant: "destructive",
          })
        } else if (error.code === 'file-invalid-type') {
          toast({
            title: "Invalid File Type",
            description: `${file.name} is not a PDF file`,
            variant: "destructive",
          })
        }
      })
    })

    // Add accepted files with order numbers
    setFiles(prev => {
      const nextOrder = prev.length > 0 ? Math.max(...prev.map(f => f.order)) + 1 : 1
      return [...prev, ...acceptedFiles.map((file, index) => ({
        file,
        id: Date.now() + Math.random() + index,
        status: 'ready',
        order: nextOrder + index
      }))]
    })
  }, [toast])

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: 20 * 1024 * 1024, // 20MB
    multiple: true
  })

  const removeFile = (id) => {
    setFiles(prev => {
      const filtered = prev.filter(f => f.id !== id)
      // Reorder remaining files to maintain sequence 1, 2, 3...
      return filtered.map((file, index) => ({
        ...file,
        order: index + 1
      }))
    })
  }

  const moveFile = (fileId, direction) => {
    setFiles(prev => {
      const fileIndex = prev.findIndex(f => f.id === fileId)
      if (fileIndex === -1) return prev
      
      const newIndex = direction === 'up' ? fileIndex - 1 : fileIndex + 1
      if (newIndex < 0 || newIndex >= prev.length) return prev
      
      const newFiles = [...prev]
      const [movedFile] = newFiles.splice(fileIndex, 1)
      newFiles.splice(newIndex, 0, movedFile)
      
      // Update order numbers to match new positions
      return newFiles.map((file, index) => ({
        ...file,
        order: index + 1
      }))
    })
  }

  const handleDragStart = (e, fileId) => {
    e.dataTransfer.setData('text/plain', fileId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e, targetFileId) => {
    e.preventDefault()
    const draggedFileId = e.dataTransfer.getData('text/plain')
    
    if (draggedFileId === targetFileId) return
    
    setFiles(prev => {
      const draggedIndex = prev.findIndex(f => f.id.toString() === draggedFileId)
      const targetIndex = prev.findIndex(f => f.id === targetFileId)
      
      if (draggedIndex === -1 || targetIndex === -1) return prev
      
      const newFiles = [...prev]
      const [draggedFile] = newFiles.splice(draggedIndex, 1)
      newFiles.splice(targetIndex, 0, draggedFile)
      
      // Update order numbers to match new positions
      return newFiles.map((file, index) => ({
        ...file,
        order: index + 1
      }))
    })
  }

  const handleUpload = () => {
    if (files.length === 0) {
      toast({
        title: "No Files Selected",
        description: "Please select at least one PDF file to upload",
        variant: "destructive",
      })
      return
    }

    // Sort files by order before uploading
    const sortedFiles = [...files].sort((a, b) => a.order - b.order)
    
    const formData = new FormData()
    const documentOrder = sortedFiles.map(f => f.order)
    
    sortedFiles.forEach(({ file }) => {
      formData.append('documents', file)
    })
    
    // Add document order to form data
    formData.append('documentOrder', JSON.stringify(documentOrder))
    
    // Add AI provider selection
    formData.append('aiProvider', aiProvider)

    uploadMutation.mutate(formData)
  }

  const dropzoneClass = `
    relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
    transition-all duration-300 ease-in-out
    ${isDragActive 
      ? isDragAccept 
        ? 'border-green-400 bg-green-50 dark:bg-green-950' 
        : 'border-red-400 bg-red-50 dark:bg-red-950'
      : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950'
    }
  `

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <AnalyticsHeader />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => setLocation('/')}
              className="mr-4"
              data-testid="button-back"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Upload Lease Documents
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                Upload your PDF lease documents for AI-powered date extraction
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Upload Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Upload className="h-5 w-5 mr-2" />
                  Upload Documents
                </CardTitle>
                <CardDescription>
                  Drag and drop your PDF files or click to browse. Maximum 20MB per file.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div {...getRootProps()} className={dropzoneClass} data-testid="dropzone-upload">
                  <input {...getInputProps()} />
                  <div className="space-y-4">
                    {isDragActive ? (
                      <motion.div
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        className="flex flex-col items-center"
                      >
                        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mb-4">
                          <Upload className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <p className="text-lg font-medium text-blue-600 dark:text-blue-400">
                          {isDragAccept ? 'Drop your PDF files here' : 'File type not supported'}
                        </p>
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center"
                      >
                        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                          <FileText className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          Drag & drop PDF files here
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          or click to browse your files
                        </p>
                        <Button variant="outline" data-testid="button-browse-files">
                          Browse Files
                        </Button>
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-6 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Selected Files ({files.length})
                      </h3>
                      {files.length > 1 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Drag to reorder • Documents will be processed in order 1, 2, 3...
                        </p>
                      )}
                    </div>
                    {[...files].sort((a, b) => a.order - b.order).map(({ file, id, status, order }, index) => (
                      <motion.div
                        key={id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="group relative flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-transparent hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-200"
                        data-testid={`file-item-${id}`}
                        draggable={files.length > 1}
                        onDragStart={(e) => handleDragStart(e, id)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, id)}
                      >
                        {/* Order Number Badge */}
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-sm font-semibold mr-3 flex-shrink-0">
                          {order}
                        </div>

                        {/* Drag Handle */}
                        {files.length > 1 && (
                          <div className="flex flex-col items-center mr-3 cursor-grab active:cursor-grabbing opacity-40 group-hover:opacity-100 transition-opacity">
                            <GripVertical className="h-5 w-5 text-gray-400" />
                          </div>
                        )}

                        {/* File Info */}
                        <div className="flex items-center flex-1 min-w-0">
                          <FileText className="h-8 w-8 text-red-600 mr-3 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatBytes(file.size)}
                            </p>
                          </div>
                        </div>

                        {/* Controls */}
                        <div className="flex items-center space-x-1">
                          {/* Status indicator */}
                          {status === 'ready' && (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          )}

                          {/* Up/Down Arrows for accessibility */}
                          {files.length > 1 && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => moveFile(id, 'up')}
                                disabled={index === 0}
                                className="opacity-60 hover:opacity-100 h-8 w-8 p-0"
                                data-testid={`button-move-up-${id}`}
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => moveFile(id, 'down')}
                                disabled={index === files.length - 1}
                                className="opacity-60 hover:opacity-100 h-8 w-8 p-0"
                                data-testid={`button-move-down-${id}`}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </>
                          )}

                          {/* Remove button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(id)}
                            className="opacity-60 hover:opacity-100 h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            data-testid={`button-remove-${id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

{/* AI Provider Selection - Removed for simplified single-provider experience */}

                {/* Upload Button */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-6 flex justify-end"
                >
                  <Button
                    onClick={handleUpload}
                    disabled={files.length === 0 || uploadMutation.isPending}
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    data-testid="button-start-extraction"
                  >
                    {uploadMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Starting Analysis...
                      </>
                    ) : (
                      <>
                        <Brain className="h-4 w-4 mr-2" />
                        Start AI Analysis
                      </>
                    )}
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Brain className="h-5 w-5 mr-2 text-blue-600" />
                  AI Processing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Zap className="h-5 w-5 text-yellow-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Intelligent Chunking</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Documents are intelligently split with context preservation
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Advanced AI Analysis</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Advanced AI extracts dates with high accuracy
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FileText className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Detailed Results</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Get date names, values, and precise document locations
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
                  Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span>File Format</span>
                  <span className="font-medium">PDF only</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Max File Size</span>
                  <span className="font-medium">20MB each</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Multiple Files</span>
                  <span className="font-medium">Supported</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Processing Time</span>
                  <span className="font-medium">~30 sec/page</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* File Not Supported Modal */}
      <Dialog open={showUnsupportedModal} onOpenChange={(open) => {
        setShowUnsupportedModal(open)
        if (!open) {
          setUnsupportedFileError(null)
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-amber-100 dark:bg-amber-900 p-4">
                <FileX className="h-12 w-12 text-amber-600 dark:text-amber-400" />
              </div>
              <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white">
                File Not Supported
              </DialogTitle>
            </div>
            <DialogDescription asChild>
              <div className="space-y-4 text-center pt-4">
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  The file <span className="font-semibold">"{unsupportedFileError?.filename}"</span> appears to be a scanned PDF without readable text.
                </p>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Unfortunately, our diligent AI is unable to read image-based PDFs. Please use an OCR tool to convert it to a text-based PDF first, or upload a different document.
                </p>
                <div className="pt-4">
                  <Button
                    onClick={() => {
                      setShowUnsupportedModal(false)
                      setUnsupportedFileError(null)
                    }}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300"
                    data-testid="button-close-unsupported-modal"
                  >
                    Got It
                  </Button>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  )
}